<template>
    <div class="regex-page">
        <el-card class="pattern-card" shadow="never">
            <div class="pattern-row">
                <span class="slash">/</span>
                <el-input
                    v-model="pattern"
                    class="pattern-input"
                    placeholder="输入正则表达式，例如 (\w+)\s+(\d+)"
                    clearable
                    spellcheck="false">
                </el-input>
                <span class="slash">/</span>
                <el-checkbox-group v-model="flagKeys" class="flag-group">
                    <el-checkbox-button
                        v-for="flag in flagOptions"
                        :key="flag.key"
                        :label="flag.key"
                        :title="flag.title">
                        {{ flag.label }}
                    </el-checkbox-button>
                </el-checkbox-group>
                <el-button
                    class="help-btn"
                    icon="el-icon-question"
                    title="正则语法说明"
                    @click="cheatSheetVisible = true">
                    语法
                </el-button>
            </div>
            <div class="meta-row">
                <div class="presets">
                    <span class="meta-label">常用</span>
                    <el-button
                        v-for="preset in presets"
                        :key="preset.name"
                        size="mini"
                        @click="applyPreset(preset)">
                        {{ preset.name }}
                    </el-button>
                </div>
                <div class="status">
                    <el-tag v-if="compileError" type="danger" size="mini">语法错误</el-tag>
                    <el-tag v-else-if="!pattern" type="info" size="mini">等待输入</el-tag>
                    <el-tag v-else type="success" size="mini">{{ matchSummary }}</el-tag>
                    <el-button size="mini" @click="handleClear">清空</el-button>
                </div>
            </div>
            <div v-if="compileError" class="error-line">{{ compileError }}</div>
        </el-card>

        <div class="workspace">
            <div class="left-col">
                <el-card class="pane-card" shadow="never">
                    <div slot="header" class="pane-header">
                        <div class="pane-title">
                            <span>测试文本</span>
                            <span class="hint">{{ testText.length }} 字符</span>
                        </div>
                        <div class="text-search">
                            <el-input
                                ref="searchInput"
                                v-model="searchQuery"
                                size="mini"
                                prefix-icon="el-icon-search"
                                placeholder="搜索"
                                clearable
                                @keydown.native.enter.exact.prevent="gotoSearch(1)"
                                @keydown.native.enter.shift.prevent="gotoSearch(-1)"
                                @keydown.native.esc.prevent="clearSearch">
                            </el-input>
                            <span class="search-count">{{ searchCountLabel }}</span>
                            <el-button-group>
                                <el-button
                                    size="mini"
                                    icon="el-icon-arrow-up"
                                    :disabled="!searchHits.length"
                                    @click="gotoSearch(-1)">
                                </el-button>
                                <el-button
                                    size="mini"
                                    icon="el-icon-arrow-down"
                                    :disabled="!searchHits.length"
                                    @click="gotoSearch(1)">
                                </el-button>
                            </el-button-group>
                        </div>
                    </div>
                    <div class="text-pane">
                        <pre class="text-hl" ref="highlightLayer" v-html="highlightedHtml || ' '"></pre>
                        <textarea
                            ref="textInput"
                            class="text-input"
                            v-model="testText"
                            placeholder="在此输入要匹配的文本"
                            spellcheck="false"
                            @scroll="syncScroll"
                            @keydown.ctrl.f.prevent="focusSearch"
                            @keydown.meta.f.prevent="focusSearch"></textarea>
                    </div>
                </el-card>

                <el-card class="pane-card replace-card" shadow="never">
                    <div slot="header" class="pane-header">
                        <span>替换</span>
                        <span class="hint">支持 $1、$&amp; 等替换语法</span>
                    </div>
                    <el-input
                        v-model="replacement"
                        placeholder="替换模板，例如 [$1]"
                        spellcheck="false">
                    </el-input>
                    <el-input
                        class="replace-result"
                        type="textarea"
                        :rows="5"
                        :value="replaceResult"
                        readonly
                        resize="none"
                        placeholder="替换结果会显示在这里">
                    </el-input>
                </el-card>
            </div>

            <div class="right-col">
                <el-card class="pane-card result-card" shadow="never">
                    <div slot="header" class="pane-header">
                        <span>匹配结果</span>
                        <div class="header-actions">
                            <el-button size="mini" :disabled="!matches.length" @click="copyMatches">
                                复制全部匹配
                            </el-button>
                        </div>
                    </div>
                    <div class="table-wrap">
                        <el-table
                            :data="matches"
                            height="100%"
                            size="mini"
                            highlight-current-row
                            :row-class-name="rowClassName"
                            @row-click="selectMatch">
                            <el-table-column type="index" label="#" width="48" :index="rowIndex"></el-table-column>
                            <el-table-column prop="value" label="匹配" min-width="160" show-overflow-tooltip>
                                <template slot-scope="{ row }">
                                    <code class="match-value">{{ row.value === '' ? '∅' : row.value }}</code>
                                </template>
                            </el-table-column>
                            <el-table-column label="分组" min-width="140" show-overflow-tooltip>
                                <template slot-scope="{ row }">
                                    <span v-if="!row.groups.length">-</span>
                                    <span v-else>{{ groupPreview(row) }}</span>
                                </template>
                            </el-table-column>
                        </el-table>
                    </div>
                </el-card>
            </div>
        </div>

        <el-dialog
            title="正则语法说明"
            :visible.sync="cheatSheetVisible"
            width="640px"
            append-to-body>
            <el-tabs>
                <el-tab-pane v-for="group in cheatSheet" :key="group.name" :label="group.name">
                    <el-table :data="group.items" size="mini" border>
                        <el-table-column prop="token" label="写法" width="140">
                            <template slot-scope="{ row }">
                                <code class="token">{{ row.token }}</code>
                            </template>
                        </el-table-column>
                        <el-table-column prop="meaning" label="含义"></el-table-column>
                    </el-table>
                </el-tab-pane>
            </el-tabs>
        </el-dialog>
    </div>
</template>

<script>
import {
    FLAG_OPTIONS,
    PRESETS,
    CHEATSHEET,
    compileRegex,
    collectMatches,
    applyReplace,
    highlightHtml,
    toHighlightRanges,
    collectLiteralHits,
    joinMatches
} from '@/utils/regex-tester'

const DEFAULT_PATTERN = '(\\w+)\\s+(\\d+)'
const DEFAULT_TEXT = [
    'order 1001',
    'user 42',
    'skip this line',
    'item 7'
].join('\n')

export default {
    name: 'RegexTester',
    data() {
        return {
            pattern: DEFAULT_PATTERN,
            flagKeys: ['g'],
            testText: DEFAULT_TEXT,
            replacement: '[$1=$2]',
            activeIndex: -1,
            searchQuery: '',
            searchIndex: -1,
            cheatSheetVisible: false,
            flagOptions: FLAG_OPTIONS,
            presets: PRESETS,
            cheatSheet: CHEATSHEET
        }
    },
    computed: {
        flags() {
            return this.flagKeys.join('')
        },
        compiled() {
            return compileRegex(this.pattern, this.flags)
        },
        compileError() {
            return this.compiled.error
        },
        matchBundle() {
            if (!this.compiled.ok || !this.compiled.regex) {
                return { matches: [], truncated: false }
            }
            return collectMatches(this.compiled.regex, this.testText)
        },
        matches() {
            return this.matchBundle.matches
        },
        matchSummary() {
            const count = this.matches.length
            const suffix = this.matchBundle.truncated ? '+' : ''
            const flagHint = this.flagKeys.includes('g') ? '' : '（未开 g，仅首个）'
            return `匹配 ${count}${suffix} 处${flagHint}`
        },
        highlightedHtml() {
            return highlightHtml(this.testText, [
                ...toHighlightRanges(this.matches, 're-hit', this.activeIndex),
                ...toHighlightRanges(this.searchHits, 'search-hit', this.searchIndex)
            ])
        },
        searchHits() {
            return collectLiteralHits(this.testText, this.searchQuery.trim())
        },
        searchCountLabel() {
            if (!this.searchQuery.trim()) {
                return ''
            }
            if (!this.searchHits.length) {
                return '0/0'
            }
            return `${this.searchIndex + 1}/${this.searchHits.length}`
        },
        replaceResult() {
            if (!this.compiled.ok || !this.compiled.regex) {
                return ''
            }
            const result = applyReplace(this.compiled.regex, this.testText, this.replacement)
            return result.ok ? result.value : result.error
        }
    },
    watch: {
        matches() {
            if (!this.matches.some((item) => item.index === this.activeIndex)) {
                this.activeIndex = this.matches.length ? 0 : -1
            }
        },
        searchHits() {
            if (!this.searchHits.length) {
                this.searchIndex = -1
                return
            }
            if (!this.searchHits.some((item) => item.index === this.searchIndex)) {
                this.searchIndex = 0
                this.$nextTick(this.scrollToSearch)
            }
        }
    },
    methods: {
        applyPreset(preset) {
            this.pattern = preset.pattern
            if (!this.flagKeys.includes('g')) {
                this.flagKeys = this.flagKeys.concat('g')
            }
        },
        handleClear() {
            this.pattern = ''
            this.testText = ''
            this.replacement = ''
            this.activeIndex = -1
            this.clearSearch()
        },
        clearSearch() {
            this.searchQuery = ''
            this.searchIndex = -1
        },
        focusSearch() {
            const input = this.$refs.searchInput
            if (input && input.focus) {
                input.focus()
            }
        },
        gotoSearch(step) {
            if (!this.searchHits.length) {
                return
            }
            const total = this.searchHits.length
            this.searchIndex = (this.searchIndex + step + total) % total
            this.$nextTick(this.scrollToSearch)
        },
        selectMatch(row) {
            this.activeIndex = row.index
            this.$nextTick(this.scrollToActive)
        },
        rowIndex(index) {
            return index + 1
        },
        rowClassName({ row }) {
            return row.index === this.activeIndex ? 'is-active-row' : ''
        },
        groupPreview(row) {
            return row.groups
                .map((group) => `$${group.index}=${group.value || '∅'}`)
                .join('  ')
        },
        copyMatches(event) {
            this.$clipboard(joinMatches(this.matches), event)
        },
        syncScroll() {
            const source = this.$refs.textInput
            const target = this.$refs.highlightLayer
            if (!source || !target) {
                return
            }
            target.scrollTop = source.scrollTop
            target.scrollLeft = source.scrollLeft
        },
        scrollToActive() {
            this.scrollMark('.re-hit.is-active')
        },
        scrollToSearch() {
            this.scrollMark('.search-hit.is-active')
        },
        scrollMark(selector) {
            const layer = this.$refs.highlightLayer
            if (!layer) {
                return
            }
            const mark = layer.querySelector(selector)
            if (mark && mark.scrollIntoView) {
                mark.scrollIntoView({ block: 'nearest' })
                this.syncScrollFromHighlight()
            }
        },
        syncScrollFromHighlight() {
            const source = this.$refs.highlightLayer
            const target = this.$refs.textInput
            if (!source || !target) {
                return
            }
            target.scrollTop = source.scrollTop
            target.scrollLeft = source.scrollLeft
        }
    }
}
</script>

<style scoped>
.regex-page {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    overflow: hidden;
    gap: 10px;
}

.pattern-card,
.pane-card {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.pattern-card {
    flex: 0 0 auto;
}

.pattern-card ::v-deep .el-card__body,
.pane-card ::v-deep .el-card__body {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.pattern-row {
    display: flex;
    align-items: center;
    gap: 8px;
}

.slash {
    font-family: Monaco, Menlo, Consolas, monospace;
    font-size: 22px;
    color: #909399;
    line-height: 1;
}

.pattern-input {
    flex: 1;
}

.pattern-input ::v-deep .el-input__inner {
    font-family: Monaco, Menlo, Consolas, monospace;
    font-size: 14px;
}

.flag-group {
    flex-shrink: 0;
}

.help-btn {
    flex-shrink: 0;
    margin-left: 4px;
}

.token {
    font-family: Monaco, Menlo, Consolas, monospace;
    color: #409eff;
}

.meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    margin-top: 10px;
}

.presets,
.status,
.pane-header,
.header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
}

.pane-header {
    justify-content: space-between;
    gap: 12px;
}

.pane-title,
.text-search {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
}

.text-search {
    flex: 1;
    justify-content: flex-end;
}

.text-search .el-input {
    width: 180px;
}

.search-count {
    min-width: 36px;
    color: #909399;
    font-size: 12px;
    text-align: right;
}

.meta-label,
.hint {
    color: #909399;
    font-size: 12px;
}

.error-line {
    margin-top: 8px;
    color: #f56c6c;
    font-size: 12px;
}

.workspace {
    flex: 1;
    min-height: 0;
    display: flex;
    gap: 10px;
    overflow: hidden;
}

.left-col,
.right-col {
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    gap: 10px;
}

.left-col {
    flex: 1.15;
}

.right-col {
    flex: 0.85;
}

.pane-card {
    flex: 1;
    min-height: 0;
}

.replace-card {
    flex: 0 0 auto;
}

.result-card ::v-deep .el-card__body {
    padding-bottom: 12px;
    overflow: hidden;
}

.table-wrap {
    flex: 1;
    min-height: 0;
    overflow: hidden;
}

.text-pane {
    position: relative;
    flex: 1;
    min-height: 0;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    background: #fafafa;
    overflow: hidden;
}

.text-hl,
.text-input {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    margin: 0;
    padding: 12px;
    border: 0;
    outline: none;
    resize: none;
    background: transparent;
    font: 13px/1.6 Monaco, Menlo, Consolas, monospace;
    white-space: pre-wrap;
    word-wrap: break-word;
    overflow-wrap: break-word;
    overflow: auto;
}

.text-hl {
    color: #303133;
    pointer-events: none;
}

.text-input {
    color: transparent;
    caret-color: #303133;
    z-index: 1;
}

.text-input::placeholder {
    color: #c0c4cc;
}

.text-hl ::v-deep .re-hit {
    background: #ffe58a;
    color: inherit;
    border-radius: 2px;
    padding: 0 1px;
}

.text-hl ::v-deep .re-hit.is-active {
    background: #ff9f43;
    color: #fff;
}

.text-hl ::v-deep .search-hit {
    background: #b3d8ff;
    border-radius: 2px;
}

.text-hl ::v-deep .search-hit.is-active {
    background: #409eff;
    color: #fff;
}

.text-hl ::v-deep .re-hit.search-hit {
    background: #c6f6d5;
}

.text-hl ::v-deep .re-hit.search-hit.is-active,
.text-hl ::v-deep .search-hit.re-hit.is-active {
    background: #409eff;
    color: #fff;
}

.replace-result {
    margin-top: 8px;
}

.replace-result ::v-deep .el-textarea__inner {
    font-family: Monaco, Menlo, Consolas, monospace;
}

.match-value {
    font-family: Monaco, Menlo, Consolas, monospace;
}

::v-deep .is-active-row > td {
    background: #fff7e6 !important;
}
</style>